import type { AnalyticsGeographyDto } from '@pams/shared';
import { RankedList } from '@/components/DataFigures';
import { placeLabel } from '@/lib/analyticsReport';
import { USAGE_ANALYTICS_COPY } from '../copy';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  WHERE REQUESTS COME FROM — as a place NAME, and never as a point. [ANALYTICS §5]
 *
 *  ⚠️ NO MAP AND NO LINK TO ONE. The server resolves an address to a city, a state and a country
 *  and sends nothing finer, so there is no coordinate to plot — and a link to a map service would
 *  hand that service a police system's traffic pattern with every click. An IP address locates a
 *  network, not a person, which the limits say in words.
 *
 *  ⚠️ [amended C5b §4] `lookupEnabled` IS WHETHER THE GEO DATABASE **OPENED** IN THIS PROCESS, AND
 *  WITH IT `false` THE SECTION IS ONE SENTENCE AND NO ROWS. [ANALYTICS §5] read it as "configured
 *  now" and went on listing places rolled up while it was on, under "switched off now". But the
 *  ordinary `false` is a wrong path or a mount that did not land, and a ranked list of last week's
 *  places under it reads as a section that is working. So no row, no "outside this list" count
 *  and no "could not be placed" count — each names a list that is not on the screen.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const GeographySection = ({ data }: { data: AnalyticsGeographyDto }) => {
  const copy = USAGE_ANALYTICS_COPY.geography;

  if (!data.lookupEnabled) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="geography-off">
        {copy.off}
      </p>
    );
  }

  const other = copy.other(data.otherRequests);
  const unresolved = copy.unresolved(data.unresolvedRequests);
  return (
    <div className="flex flex-col gap-3">
      {data.places.length === 0 ? (
        <p className="text-sm text-muted-foreground">{copy.none}</p>
      ) : (
        <RankedList
          rows={data.places.map((place) => ({
            /* The triple, not the label: a missing part and an empty one would join to one string. */
            key: JSON.stringify([place.city, place.state, place.country]),
            label: placeLabel(place),
            value: place.requests,
          }))}
        />
      )}
      {other || unresolved ? (
        <div className="flex flex-col gap-1 text-sm text-muted-foreground">
          {other ? <p>{other}</p> : null}
          {unresolved ? <p>{unresolved}</p> : null}
        </div>
      ) : null}
    </div>
  );
};
